import {
	type AnchorPlacement,
	type CollectionFieldAnchorManualFieldValue,
	type CollectionModelFieldAnchorValue,
} from '@ikegps/core-api';
import {
	CollectionModelFieldAnchor,
	CollectionModelFieldSubFormInstance,
	CollectionModelFieldVector,
	IkeAngle,
	truthy,
	TWO_PI,
} from '@ikegps/core-shared';
import { override, makeObservable } from 'mobx';
import { T, Translate } from '../../i18n';
import { BearingInputState } from './bearing.input.state';
import { DistanceInputState } from './distance.input.state';
import { type SelectInputOption, SelectInputState } from './input.select';
import { InputState } from './input.state';

const PLACEMENT_TO_SPANS_COUNT_MAP: {
	[key in AnchorPlacement]: number;
} = {
	Inline: 1,
	Bisect: 2,
	Other: 0,
};
export class AnchorInputState extends InputState<CollectionModelFieldAnchorValue> {
	distance: DistanceInputState;
	bearing: BearingInputState;
	pitch: number | null = null;
	placement: SelectInputState<AnchorPlacement>;
	relatedSpans: [SelectInputState<string>, SelectInputState<string>];
	cachedSpanFields: CollectionModelFieldVector[] = [];
	cachedBearing: number = 0;

	@override override get value(): CollectionFieldAnchorManualFieldValue | null {
		if (this.distance.value == null && this.bearing.value == null) {
			return null;
		}

		return {
			bearing: this.bearing.value as number,
			distance: this.distance.value as number,
			pitch: this.pitch as number,
			collectionTool: 'VECTOR',
			placement: this.placement.value as AnchorPlacement,
			// we only want to return the correct number of spans based off the placement, ie. inline should only return 1 span
			relatedSpanIds: this.relatedSpans
				.map((x) => x.value)
				.slice(0, PLACEMENT_TO_SPANS_COUNT_MAP[this.placement.value as AnchorPlacement]) as string[],
		};
	}

	static equal(value1: CollectionFieldAnchorManualFieldValue, value2: CollectionFieldAnchorManualFieldValue) {
		if (value1 === value2) {
			return true;
		}

		if (value1 == null || value2 == null) {
			return false;
		}

		return (
			DistanceInputState.equal(value1.distance, value2.distance) &&
			BearingInputState.equal(value1.bearing, value2.bearing) &&
			value1.pitch === value2.pitch &&
			value1.placement === value2.placement
		);
	}

	constructor(private field: CollectionModelFieldAnchor) {
		super(field.schema.displayName);

		makeObservable(this);

		this.isLabelShown = false;

		this.distance = new DistanceInputState('Distance');
		this.bearing = new BearingInputState('Bearing');
		this.placement = new SelectInputState('Placement');

		this.placement.setOptions([
			{
				key: Translate(T.ANCHOR_ALIGNMENT_INLINE),
				value: 'Inline',
			},
			{
				key: Translate(T.ANCHOR_ALIGNMENT_BISECT),
				value: 'Bisect',
			},
			{
				key: Translate(T.ANCHOR_ALIGNMENT_OTHER),
				value: 'Other',
			},
		]);

		this.relatedSpans = [
			new SelectInputState(Translate(T.ACCURATE_ANCHOR_RELATED_SPAN_TITLE, 'A')),
			new SelectInputState(Translate(T.ACCURATE_ANCHOR_RELATED_SPAN_TITLE, 'B')),
		];

		this.reaction(
			() => this.bearing.value,
			() => {
				if (this.bearing.focused) {
					this.placement.setValue('Other');
					this.relatedSpans.forEach((x) => x.setValue(null));
				}
			}
		);
	}

	populateRelatedSpansOptions(fields: CollectionModelFieldVector[]) {
		let options: SelectInputOption[] = [];
		this.cachedSpanFields = fields;

		fields.forEach((x) => {
			// Skip any vector fields on the same subform level & type
			// @TODO update logic to use anchor base form when available
			if (x.parent && x.parent.schema.id !== this.field.parent.schema.id) {
				const spanLabel =
					x.parent instanceof CollectionModelFieldSubFormInstance ? x.parent.title : x.schema.displayName;

				options.push({
					key: spanLabel ?? Translate(T.UNKNOWN),
					value: x.id,
				});
			}
		});

		this.relatedSpans.forEach((x) => x.setOptions(options));
	}

	@override override setValue(val: CollectionFieldAnchorManualFieldValue) {
		if (val == null) {
			this.distance.setValue(null);
			this.bearing.setValue(null);
			this.pitch = null;
			this.placement.setValue(null);
			this.setError(null);
			return;
		}

		this.distance.setValue(val.distance);
		this.pitch = val.pitch;
		this.placement.setValue(val.placement);

		const bearing = this.bearing.focused ? val.bearing : this.calculateBearingFromPlacement() || val.bearing;

		if (bearing !== this.cachedBearing) {
			this.bearing.setValue(bearing);
			this.cachedBearing = bearing;
		}

		this.relatedSpans[0].setValue(val.relatedSpanIds?.[0] ?? null);
		this.relatedSpans[1].setValue(val.relatedSpanIds?.[1] ?? null);

		switch (val.placement) {
			case 'Bisect':
				this.relatedSpans.forEach((x) => x.setDisabled(false));
				break;

			case 'Inline':
				this.relatedSpans[0].setDisabled(false);
				this.relatedSpans[1].setDisabled(true);
				break;

			default:
				this.relatedSpans.forEach((x) => x.setDisabled(true));
		}

		if (val.distance == null && val.bearing == null) {
			this.setError(null);
		} else if (val.distance == null) {
			this.setError(T.DISTANCE_MISSING);
		} else if (val.bearing == null) {
			this.setError(T.BEARING_MISSING);
		} else {
			this.setError(null);
		}
	}

	calculateBearingFromPlacement() {
		let nextBearing: number = 0;

		const placement = this.placement.value;
		if (!placement) {
			return nextBearing;
		}

		const relatedSpanFieldIds = this.relatedSpans.map((x) => x.value).filter(truthy);
		if (!relatedSpanFieldIds.length) {
			return nextBearing;
		}

		if (placement === 'Bisect') {
			if (relatedSpanFieldIds.length < 2) {
				this.setError(T.ACCURATE_ANCHOR_RELATED_SPAN_NO_SELECTION_BISECT);
			}

			const spanRadians = relatedSpanFieldIds
				.map((fieldId) => this.field.collection.getFieldDeep<CollectionModelFieldVector>(fieldId))
				.map((x) => {
					const value = x?.value?.bearing;

					if (!value) {
						this.setError(T.ACCURATE_ANCHOR_RELATED_SPAN_NO_VALUE);
					}

					return value;
				})
				.sort();

			// ensure all values are defined and are numbers
			if (spanRadians.some((x) => typeof x !== 'number')) {
				return nextBearing;
			}

			// We can ensure that both spans exist because of the length check above
			const a = IkeAngle.toDegrees(spanRadians[0]!);
			const b = IkeAngle.toDegrees(spanRadians[1]!);

			// find the angle between the two bearings
			const theta = a - b;

			// find the bisect angle of theta relative to a.
			// basically we derive what half theta is (θ/2) and minus that value
			// from a to derive what the bisector is
			const bisect = a - theta / 2;

			// We then want to ensure the anchor is placed on convex side of the angle
			// so we add 180 deg if θ < 180
			const result = Math.abs(theta) < 180 ? bisect + 180 : bisect;

			nextBearing = IkeAngle.toRadians(result) % TWO_PI;
		}

		if (placement === 'Inline') {
			if (relatedSpanFieldIds[0]) {
				const relatedSpan = this.field.collection.getFieldDeep<CollectionModelFieldVector>(relatedSpanFieldIds[0]);
				const bearing = relatedSpan?.value?.bearing;

				if (bearing != null && !isNaN(bearing)) {
					nextBearing = (IkeAngle.toRadians(180) + bearing) % TWO_PI;
				} else {
					this.setError(T.ACCURATE_ANCHOR_RELATED_SPAN_NO_VALUE);
				}
			} else {
				this.setError(T.ACCURATE_ANCHOR_RELATED_SPAN_NO_SELECTION_INLINE);
			}
		}

		return nextBearing;
	}

	override hasError() {
		return (
			super.hasError() ||
			this.distance.hasError() ||
			this.bearing.hasError() ||
			this.placement.hasError() ||
			this.relatedSpans.some((x) => x.hasError())
		);
	}

	@override override get error() {
		// Don't show an error message if one is already being shown
		if (this.distance.hasError() || this.bearing.hasError() || this.placement.hasError()) {
			return '';
		}

		if (!this.distance.value) {
			// distance is empty
			if (this.placement.value || (this.bearing.value && this.bearing.value > 0)) {
				// placement has a value or the bearing has an actual value
				return T.DISTANCE_MISSING;
			}
			return this._error; // everything is empty
		} else if (this.distance.value && this.bearing.value === null) {
			// does bearing have an actual value
			return T.BEARING_MISSING;
		} else if (this.placement.value === null && this.distance.value) {
			// does placement have a valid value
			return T.PLACEMENT_MISSING;
		}

		// everything has a valid value
		return this._error;
	}

	override set error(msg: string) {
		this._error = msg || '';
	}

	override destroy() {
		super.destroy();
		this.distance.destroy();
	}
}
